/**
 * Core Connect Four game engine
 * Handles game state, move validation, win detection, and game logic
 */

import { IGameState, IPosition, IWinningLine, PieceColor, GameStatus } from '../types/game-types';

const ROWS = 6;
const COLUMNS = 7;
const WIN_COUNT = 4;

export class FourInARowEngine {
  private gameState: IGameState;

  constructor() {
    this.gameState = this.createInitialState();
  }

  /**
   * Creates initial game state with empty board
   */
  private createInitialState(): IGameState {
    return {
      board: Array(ROWS)
        .fill(null)
        .map(() => Array(COLUMNS).fill(null)),
      currentPlayer: Math.random() < 0.5 ? 1 : 2,
      status: GameStatus.NotStarted,
      winner: null,
      winningLine: null,
      moveHistory: [],
    };
  }

  /**
   * Gets current game state (read-only)
   */
  public getGameState(): Readonly<IGameState> {
    return { ...this.gameState };
  }

  /**
   * Creates a deep copy of the current engine instance
   * Used for AI move simulation without affecting the actual game state
   */
  public cloneEngine(): FourInARowEngine {
    const clonedEngine = new FourInARowEngine();

    // Deep clone the game state
    clonedEngine.gameState = {
      board: this.gameState.board.map(row => [...row]),
      currentPlayer: this.gameState.currentPlayer,
      status: this.gameState.status,
      winner: this.gameState.winner,
      winningLine: this.gameState.winningLine
        ? {
            positions: this.gameState.winningLine.positions.map(pos => ({ ...pos })),
            direction: this.gameState.winningLine.direction,
          }
        : null,
      moveHistory: this.gameState.moveHistory.map(pos => ({ ...pos })),
    };

    return clonedEngine;
  }

  /**
   * Simulates a move without changing the actual game state
   * Returns a new engine instance with the move applied
   * @param column - Column index (0-6)
   * @returns New engine instance with the move applied, or null if move is invalid
   */
  public simulateMove(column: number): FourInARowEngine | null {
    // Validate move first
    const validation = this.validateMove(column);
    if (!validation.isValid) {
      return null;
    }

    // Create a clone and apply the move
    const simulatedEngine = this.cloneEngine();
    const result = simulatedEngine.makeMoveInternal(column, true); // silent mode

    return result.success ? simulatedEngine : null;
  }

  /**
   * Starts a new game
   */
  public startNewGame(): void {
    this.gameState = this.createInitialState();
    this.gameState.status = GameStatus.InProgress;
  }

  /**
   * Makes a move in the specified column
   * @param column - Column index (0-6)
   * @returns Object with success status and optional error message
   */
  public makeMove(column: number): { success: boolean; error?: string } {
    return this.makeMoveInternal(column, false);
  }

  /**
   * Internal method for making moves with optional silent mode
   * @param column - Column index (0-6)
   * @param silent - If true, suppresses console logging
   * @returns Object with success status and optional error message
   */
  private makeMoveInternal(
    column: number,
    silent: boolean = false
  ): { success: boolean; error?: string } {
    // Validate move
    const validation = this.validateMove(column);
    if (!validation.isValid) {
      return { success: false, error: validation.error };
    }

    // Find available row
    const row = this.findAvailableRow(column);

    // Place piece - map player number to piece color
    const pieceColor = this.gameState.currentPlayer === 1 ? PieceColor.Red : PieceColor.Yellow;
    this.gameState.board[row][column] = pieceColor;

    // Add to move history
    this.gameState.moveHistory.push({ row, column });

    // Check for win
    const winningLine = this.checkWin(row, column);
    if (winningLine) {
      this.gameState.status = GameStatus.Won;
      this.gameState.winner = this.gameState.currentPlayer;
      this.gameState.winningLine = winningLine;
      return { success: true };
    }

    // Check for draw
    if (this.isBoardFull()) {
      this.gameState.status = GameStatus.Draw;
      return { success: true };
    }

    // Switch player
    this.gameState.currentPlayer = this.gameState.currentPlayer === 1 ? 2 : 1;

    return { success: true };
  } /**
   * Validates if a move is legal
   */
  private validateMove(column: number): { isValid: boolean; error?: string } {
    if (this.gameState.status !== GameStatus.InProgress) {
      return { isValid: false, error: 'Game is not in progress' };
    }

    if (column < 0 || column >= COLUMNS) {
      return { isValid: false, error: 'Invalid column' };
    }

    if (this.gameState.board[0][column] !== null) {
      return { isValid: false, error: 'Column is full' };
    }

    return { isValid: true };
  }

  /**
   * Finds the lowest available row in a column
   */
  private findAvailableRow(column: number): number {
    for (let row = ROWS - 1; row >= 0; row--) {
      if (this.gameState.board[row][column] === null) {
        return row;
      }
    }
    return -1;
  }

  /**
   * Checks if the board is completely full
   */
  private isBoardFull(): boolean {
    return this.gameState.board[0].every(cell => cell !== null);
  }

  /**
   * Checks for a winning condition from the last move
   */
  private checkWin(row: number, column: number): IWinningLine | null {
    const pieceColor = this.gameState.board[row][column];
    if (!pieceColor) return null;

    // Check all four directions: horizontal, vertical, diagonal
    const directions = [
      { row: 0, col: 1, name: 'horizontal' as const },
      { row: 1, col: 0, name: 'vertical' as const },
      { row: 1, col: 1, name: 'diagonal-descending' as const },
      { row: 1, col: -1, name: 'diagonal-ascending' as const },
    ];

    for (const direction of directions) {
      const winningLine = this.checkDirection(
        row,
        column,
        direction.row,
        direction.col,
        direction.name
      );
      if (winningLine) {
        return winningLine;
      }
    }

    return null;
  }

  /**
   * Checks for win in a specific direction
   */
  private checkDirection(
    startRow: number,
    startCol: number,
    deltaRow: number,
    deltaCol: number,
    directionName: IWinningLine['direction']
  ): IWinningLine | null {
    const pieceColor = this.gameState.board[startRow][startCol];
    const positions: IPosition[] = [{ row: startRow, column: startCol }];

    // Check in positive direction
    let row = startRow + deltaRow;
    let col = startCol + deltaCol;
    while (
      row >= 0 &&
      row < ROWS &&
      col >= 0 &&
      col < COLUMNS &&
      this.gameState.board[row][col] === pieceColor
    ) {
      positions.push({ row, column: col });
      row += deltaRow;
      col += deltaCol;
    }

    // Check in negative direction
    row = startRow - deltaRow;
    col = startCol - deltaCol;
    while (
      row >= 0 &&
      row < ROWS &&
      col >= 0 &&
      col < COLUMNS &&
      this.gameState.board[row][col] === pieceColor
    ) {
      positions.unshift({ row, column: col });
      row -= deltaRow;
      col -= deltaCol;
    }

    // Check if we have enough pieces for a win
    if (positions.length >= WIN_COUNT) {
      return {
        positions: positions.slice(0, WIN_COUNT),
        direction: directionName,
      };
    }

    return null;
  }

  /**
   * Gets available columns for moves
   */
  public getAvailableColumns(): number[] {
    const availableColumns: number[] = [];
    for (let col = 0; col < COLUMNS; col++) {
      if (this.gameState.board[0][col] === null) {
        availableColumns.push(col);
      }
    }
    return availableColumns;
  }

  /**
   * Undoes the last move (for AI simulation)
   */
  public undoLastMove(): void {
    if (this.gameState.moveHistory.length === 0) return;

    const lastMove = this.gameState.moveHistory.pop()!;
    this.gameState.board[lastMove.row][lastMove.column] = null;

    // Reset game state
    this.gameState.status = GameStatus.InProgress;
    this.gameState.winner = null;
    this.gameState.winningLine = null;
    this.gameState.currentPlayer = this.gameState.currentPlayer === 1 ? 2 : 1;
  }

  /**
   * Evaluates the current board position for AI
   */
  public evaluatePosition(): number {
    // Simple evaluation: +100 for win, -100 for loss, 0 for neutral
    if (this.gameState.status === GameStatus.Won) {
      return this.gameState.winner === 2 ? 100 : -100; // Assuming AI is player 2
    }
    if (this.gameState.status === GameStatus.Draw) {
      return 0;
    }

    // Count potential winning lines for each player
    let score = 0;
    score += this.countPotentialWins(PieceColor.Yellow) * 10; // AI pieces
    score -= this.countPotentialWins(PieceColor.Red) * 10; // Human pieces

    return score;
  }

  /**
   * Counts potential winning lines for a color
   */
  private countPotentialWins(color: PieceColor): number {
    let count = 0;
    const directions = [
      { row: 0, col: 1 }, // horizontal
      { row: 1, col: 0 }, // vertical
      { row: 1, col: 1 }, // diagonal
      { row: 1, col: -1 }, // anti-diagonal
    ];

    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLUMNS; col++) {
        for (const dir of directions) {
          count += this.countLineScore(row, col, dir.row, dir.col, color);
        }
      }
    }

    return count;
  }

  /**
   * Scores a potential line for AI evaluation
   */
  private countLineScore(
    startRow: number,
    startCol: number,
    deltaRow: number,
    deltaCol: number,
    color: PieceColor
  ): number {
    let ownPieces = 0;
    let emptySpaces = 0;
    let blocked = false;

    for (let i = 0; i < WIN_COUNT; i++) {
      const row = startRow + i * deltaRow;
      const col = startCol + i * deltaCol;

      if (row < 0 || row >= ROWS || col < 0 || col >= COLUMNS) {
        blocked = true;
        break;
      }

      const cell = this.gameState.board[row][col];
      if (cell === color) {
        ownPieces++;
      } else if (cell === null) {
        emptySpaces++;
      } else {
        blocked = true;
        break;
      }
    }

    if (blocked || ownPieces + emptySpaces < WIN_COUNT) {
      return 0;
    }

    // Score based on how many pieces are already in line
    switch (ownPieces) {
      case 4:
        return 1000; // Winning line
      case 3:
        return 10; // Three in a row
      case 2:
        return 2; // Two in a row
      case 1:
        return 1; // One piece
      default:
        return 0;
    }
  }
}
